import { useEffect } from 'react'
import { useAppStore } from '@store/appStore'

interface FrameWorldPosition {
  frameIndex: number
  worldX: number
  worldY: number
}

interface ImageDimensions {
  imageWidth: number
  imageHeight: number
  displayWidth: number
  displayHeight: number
}

interface UseInitialViewportSetupParams {
  imageDimensions: ImageDimensions | null
  viewportSize: { width: number; height: number }
  frameWorldPositions: FrameWorldPosition[]
  displayHeightForFrames: number
  shopId: string
  currentSectionFramesPath: string | null
}

/**
 * Hook to set initial zoom and viewport position when dataset loads
 * Centers the "holy red dot" (screen center) on frame 0
 */
export function useInitialViewportSetup({
  imageDimensions,
  viewportSize,
  frameWorldPositions,
  displayHeightForFrames,
  shopId,
  currentSectionFramesPath
}: UseInitialViewportSetupParams): void {
  useEffect(() => {
    const state = useAppStore.getState()
    
    const isInitialLoad = state.zoomScale === 1.0 && imageDimensions?.displayWidth && imageDimensions.displayWidth > 0
    const isNewDataset = imageDimensions?.displayWidth && imageDimensions.displayWidth > 0 && viewportSize.width > 0
    
    console.log(`🎬 [useInitialViewportSetup] Effect running:`, {
      isInitialLoad,
      isNewDataset,
      currentZoom: state.zoomScale.toFixed(2),
      manuallySelected: state.manuallySelectedFrameIndex,
      viewport: `${viewportSize.width}×${viewportSize.height}`
    })
    
    if (isInitialLoad || isNewDataset) {
      console.log(`⚠️ [useInitialViewportSetup] RECALCULATING ZOOM (isInitialLoad=${isInitialLoad}, isNewDataset=${isNewDataset})`)
      
      // Calculate fit-to-contain zoom: use Math.min to ensure entire frame is visible
      // "zoom must fill the width or height the better of both"
      const frameWidth = imageDimensions?.displayWidth || displayWidthForFrames
      const frameHeight = imageDimensions?.displayHeight || displayHeightForFrames
      const zoomWidth = viewportSize.width / frameWidth
      const zoomHeight = viewportSize.height / frameHeight
      const fitZoom = Math.min(zoomWidth, zoomHeight)
      
      console.log(`📐 [useInitialViewportSetup] Fit-to-contain zoom calculation:`, {
        viewport: `${viewportSize.width}×${viewportSize.height}`,
        frame: `${frameWidth}×${frameHeight}`,
        zoomWidth: zoomWidth.toFixed(3),
        zoomHeight: zoomHeight.toFixed(3),
        fitZoom: fitZoom.toFixed(3)
      })
      
      // Center on selected frame (or frame 0 if none selected)
      const targetFrameIndex = state.manuallySelectedFrameIndex ?? 0
      const screenCenterX = viewportSize.width / 2
      const screenCenterY = viewportSize.height / 2
      
      // Get target frame's world position
      const targetWorldPosition = frameWorldPositions.length > targetFrameIndex ? frameWorldPositions[targetFrameIndex] : frameWorldPositions[0]
      const frameCenterX = targetWorldPosition?.worldX ?? 0
      const frameCenterY = targetWorldPosition?.worldY ?? 0
      
      // To center red dot on frame: viewportWorldPosition = frameCenter - screenCenter / zoom
      const viewportX = frameCenterX - screenCenterX / fitZoom
      const viewportY = frameCenterY - screenCenterY / fitZoom
      
      console.log(`🎯 [useInitialViewportSetup] Centering on frame ${targetFrameIndex} at (${frameCenterX.toFixed(1)}, ${frameCenterY.toFixed(1)}) with zoom ${fitZoom.toFixed(3)}`)
      state.setZoomAndViewport(fitZoom, viewportX, viewportY)
    }
  }, [
    imageDimensions?.imageWidth, 
    imageDimensions?.imageHeight, 
    imageDimensions?.displayWidth, 
    imageDimensions?.displayHeight, 
    viewportSize.width, 
    viewportSize.height, 
    shopId, 
    frameWorldPositions.length, 
    currentSectionFramesPath,
    displayHeightForFrames
  ])
}

/**
 * Hook to update viewport size on mount and window resize
 */
export function useViewportResize(
  containerRef: React.RefObject<HTMLDivElement>,
  showDebugOverlay: boolean
): void {
  useEffect(() => {
    const updateViewportSize = () => {
      const state = useAppStore.getState()
      
      console.log(`📏 [useViewportResize] Resize triggered, zoom: ${state.zoomScale.toFixed(2)}x, selected frame: ${state.manuallySelectedFrameIndex}`)
      
      // Use actual container dimensions if available, otherwise fall back to window
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect()
        state.setViewportSize(rect.width, rect.height)
      } else {
        // Fallback: Image viewer height depends on debug overlay state
        const viewerHeight = showDebugOverlay ? window.innerHeight * 0.8 : window.innerHeight
        state.setViewportSize(window.innerWidth, viewerHeight)
      }
      
      // Re-center viewport if a frame is manually selected (e.g., after fullscreen toggle)
      // This ensures the "holy red dot" stays aligned with the selected frame
      if (state.manuallySelectedFrameIndex !== null && state.frameWorldPositions.length > 0) {
        console.log(`🎯 [useViewportResize] Re-centering frame ${state.manuallySelectedFrameIndex} after resize`)
        state.centerViewportOnFrame(state.manuallySelectedFrameIndex)
      }
    }

    updateViewportSize()
    window.addEventListener('resize', updateViewportSize)
    return () => window.removeEventListener('resize', updateViewportSize)
  }, [showDebugOverlay, containerRef])
}
